import React, { useEffect, useRef, useState } from 'react';
import { Save, Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { useToast } from '../../hooks/use-toast';
import { adminApi, uploadToCloudinary } from '../../lib/api';

const FIELDS = [
  { name: 'phone', label: 'Phone Number', placeholder: '0449 995 495' },
  { name: 'email', label: 'Contact Email', placeholder: 'mf.maydenfilms@gmail.com' },
  { name: 'location', label: 'Location', placeholder: 'Cranbourne, Victoria' },
  { name: 'instagram', label: 'Instagram URL', placeholder: 'https://instagram.com/mayden_films' },
  { name: 'facebook', label: 'Facebook URL', placeholder: 'https://facebook.com/maydenfilms' },
  { name: 'google_review', label: 'Google Review Link', placeholder: 'https://g.page/r/.../review' },
  { name: 'tagline', label: 'Tagline / Hero Headline', placeholder: 'Make Your Event Look and Feel Unforgettable' },
  { name: 'hero_subtitle', label: 'Hero Subtitle', type: 'textarea', placeholder: 'Premium Glam Booth experience across Southeast Melbourne...' },
  { name: 'about_mission', label: 'About / Mission Statement', type: 'textarea' },
  { name: 'abn', label: 'ABN (Australian Business Number)', placeholder: 'e.g. 12 345 678 901 — shown discreetly in the footer when set' },
];

const SettingsAdmin = () => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);
  const { toast } = useToast();

  useEffect(() => {
    (async () => {
      try {
        const settings = await adminApi.getSettings();
        setData(settings);
      } catch (e) {
        toast({ title: 'Error', description: e.message, variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    })();
  }, [toast]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.updateSettings(data);
      toast({ title: 'Settings saved' });
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Max 5 MB', variant: 'destructive' });
      return;
    }
    setUploadingLogo(true);
    try {
      const { url } = await uploadToCloudinary(file, 'maydenfilms/branding', 'image');
      const next = { ...data, logo_url: url };
      setData(next);
      await adminApi.updateSettings(next);
      toast({ title: 'Logo uploaded and saved' });
    } catch (err) {
      toast({ title: 'Upload failed', description: err.message, variant: 'destructive' });
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const removeLogo = async () => {
    const next = { ...data, logo_url: '' };
    setData(next);
    try {
      await adminApi.updateSettings(next);
      toast({ title: 'Logo removed — using text wordmark' });
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  if (loading) return <div className="text-gray-400">Loading...</div>;

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#EADBC8] mb-2">Site Settings</h1>
        <p className="text-gray-400">Contact info, social links, and site-wide text</p>
      </div>

      <Card className="bg-[#2A2420] border-[#C5A880]/20 mb-6">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <ImageIcon size={18} className="text-[#C5A880]" />
            <h2 className="text-lg font-semibold text-[#EADBC8]">Site Logo</h2>
          </div>
          <p className="text-[#EADBC8]/60 text-sm mb-4">
            Upload a transparent PNG or SVG (max 5 MB). When set, this replaces the "Mayden Films" text wordmark in the header &amp; footer. Leave empty to use the text wordmark.
          </p>

          {data.logo_url ? (
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="bg-[#1A1612] border border-[#C5A880]/20 rounded-md p-6 flex items-center justify-center min-w-[200px]">
                <img
                  src={data.logo_url}
                  alt="Current logo"
                  className="max-h-16 max-w-[180px] object-contain"
                  data-testid="logo-preview"
                />
              </div>
              <div className="flex flex-col gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => logoInputRef.current?.click()}
                  disabled={uploadingLogo}
                  className="border-[#C5A880]/30 text-[#C5A880] hover:bg-[#C5A880]/10"
                  data-testid="replace-logo-btn"
                >
                  {uploadingLogo ? (
                    <Loader2 size={14} className="mr-2 animate-spin" />
                  ) : (
                    <Upload size={14} className="mr-2" />
                  )}
                  Replace logo
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={removeLogo}
                  disabled={uploadingLogo}
                  className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                  data-testid="remove-logo-btn"
                >
                  <X size={14} className="mr-2" />
                  Remove (use text)
                </Button>
              </div>
            </div>
          ) : (
            <Button
              type="button"
              onClick={() => logoInputRef.current?.click()}
              disabled={uploadingLogo}
              className="bg-[#C5A880] hover:bg-[#D4BC95] text-[#1F1A17] font-semibold"
              data-testid="upload-logo-btn"
            >
              {uploadingLogo ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload size={16} className="mr-2" />
                  Upload Logo
                </>
              )}
            </Button>
          )}

          <input
            ref={logoInputRef}
            type="file"
            accept="image/png,image/svg+xml,image/webp,image/jpeg"
            onChange={handleLogoUpload}
            className="hidden"
            data-testid="logo-file-input"
          />
        </CardContent>
      </Card>

      <Card className="bg-[#2A2420] border-[#C5A880]/20">
        <CardContent className="p-6 space-y-5">
          {FIELDS.map((f) => (
            <div key={f.name}>
              <label className="block text-[#EADBC8] text-sm font-medium mb-2">{f.label}</label>
              {f.type === 'textarea' ? (
                <Textarea
                  value={data[f.name] || ''}
                  onChange={(e) => setData((prev) => ({ ...prev, [f.name]: e.target.value }))}
                  placeholder={f.placeholder}
                  rows={3}
                  className="bg-[#1A1612] border-[#C5A880]/20 text-[#EADBC8]"
                  data-testid={`settings-${f.name}`}
                />
              ) : (
                <Input
                  value={data[f.name] || ''}
                  onChange={(e) => setData((prev) => ({ ...prev, [f.name]: e.target.value }))}
                  placeholder={f.placeholder}
                  className="bg-[#1A1612] border-[#C5A880]/20 text-[#EADBC8]"
                  data-testid={`settings-${f.name}`}
                />
              )}
            </div>
          ))}

          <div className="pt-4 border-t border-[#C5A880]/20">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#C5A880] hover:bg-[#D4BC95] text-[#1F1A17] font-semibold"
              data-testid="save-settings-btn"
            >
              <Save size={18} className="mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsAdmin;
